import type { EnterpriseWorkspaceRoutes } from 'v5/core/routing/appRouting';
import type { AppRouting } from 'v5/features/core/routing';

/**
 * Bridges legacy `myRouting.forWorkspace` URL builders to the {@link EnterpriseWorkspaceRoutes}
 * shape used by enterprise routing (`appRouting.org().workspace()`).
 *
 * **Why it exists:** `useAppWorkspaceRouting()` exposes a single API for **browsing chrome**
 * (list roots, breadcrumbs). It picks this adapter on legacy URLs and enterprise builders on
 * `/w/...` URLs — based on pathname, not the feature flag.
 *
 * **Entity links:** links in the browsing scope (a contact's page) go through it via
 * `useBrowsingWorkspaceRoutes`; a foreign entity's link uses `entityWorkspaceRouting`.
 *
 * **Transformation period:** While both URL worlds coexist, this adapter is the legacy side of
 * that union. Legacy org URLs stay `/app/:app_id/...`; enterprise URLs use
 * `/app/:app_id/w/:workspaceId/...`.
 * The same component code keeps working in both worlds as long as enterprise route entries
 * (and any dual `path` arrays on v1 route shells) register those URLs in React Router.
 *
 * **Only what both worlds route:** a page with no workspace URL is not in the shape. Productions
 * and shortlists live under a team (`useAppProductionsRouting`, `useAppShortlistsRouting`); a
 * single booking, the calendar home and reconciliations only have legacy builders.
 *
 * **Link generation only:** Does not participate in route matching. Matching incoming URLs
 * still belongs in the router layer (e.g. enterprise routes in `Routes.jsx`, settings dual paths).
 *
 * **Removal:** Safe to delete once legacy workspace URLs are dropped and all callers use
 * `appRouting` directly without the adapter.
 */
export function adaptLegacyWorkspaceRoutingForLinks(
  legacy: ReturnType<AppRouting['forWorkspace']>
): EnterpriseWorkspaceRoutes {
  const resources = legacy.resources();
  const contacts = legacy.contacts();
  const callouts = legacy.callouts();
  const bookings = legacy.bookings();

  return {
    pathPrefix: legacy.root,
    dashboard: () => `${legacy.root()}/dashboard`,
    resources: {
      root: resources.root,
      forResourceType: resources.item
    },
    contacts: {
      detail: contacts.detail,
      tab: contacts.tab
    },
    callouts: {
      root: callouts.root,
      detail: callouts.detail,
      publicCallout: callouts.publicCallout
    },
    bookings: {
      root: bookings.root,
      type: bookings.type
    },
    calendar: {
      resources: {
        home: () => `${legacy.root()}/calendar/resources`
      },
      productions: {
        home: () => `${legacy.root()}/calendar/productions`
      }
    },
    // Legacy tenants have one organisation-wide reporting page.
    reporting: () => `${legacy.root()}/reporting`,
    team: () => {
      throw new Error('Legacy routing has no team scope');
    }
  };
}
